import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Progress,
  Select,
  Skeleton,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdChevronRight, MdRefresh } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageLayoutWithHeaderNav from "../PageLayoutWithHeaderNav";
import {
  getAssociateCoachingDashboard,
  getAssociateCoachingPeriods,
  getMyCoachingFeedback,
  getMyCoachingGoals,
  refreshAssociateCoachingSummary,
} from "../../services/associateCoaching";

const JOB_LABELS = { sales: "Sales", service_advisor: "Service", finance: "Finance" };
const percentage = (value) => value == null ? "—" : `${Math.round(Number(value))}%`;

function DimensionGrid({ title, description, dimensions }) {
  if (!dimensions.length) return null;
  return (
    <Box>
      <Heading size="md">{title}</Heading>
      {description && <Text mt={1} color="gray.500" fontSize="sm">{description}</Text>}
      <Grid mt={3} templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={3}>
        {dimensions.map((dimension) => (
          <Box key={`${dimension.dimension_type}:${dimension.dimension_id}`} bg="white" borderWidth="1px" borderRadius="lg" p={4}>
            <Flex justify="space-between" gap={3}><Text fontWeight="semibold">{dimension.label}</Text><Text fontWeight="bold">{percentage(dimension.score)}</Text></Flex>
            <Progress mt={3} value={dimension.score || 0} size="sm" borderRadius="full" colorScheme="blue" />
            <Text mt={2} color="gray.500" fontSize="xs">{dimension.scorecard_count} threads · {percentage(dimension.coverage)} coverage</Text>
          </Box>
        ))}
      </Grid>
    </Box>
  );
}

function Narrative({ summary, onThread }) {
  if (!summary) return null;
  return (
    <Box bg="blue.50" borderWidth="1px" borderColor="blue.100" borderRadius="xl" p={5}>
      <Flex justify="space-between" align="center" mb={3}><Heading size="md">Your coaching brief</Heading>{summary.stale && <Badge colorScheme="orange">Updating</Badge>}</Flex>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
        {[["Strengths", "strengths"], ["Opportunities", "opportunities"], ["Improving", "improving"], ["Next actions", "actions"]].map(([label, key]) => (
          <Box key={key} bg="white" borderRadius="lg" p={4}>
            <Text fontSize="xs" color="blue.700" fontWeight="bold" textTransform="uppercase">{label}</Text>
            <VStack align="stretch" spacing={3} mt={2}>
              {(summary.output[key] || []).map((item, index) => (
                <Box key={`${key}-${index}`}><Text fontSize="sm">{item.text}</Text><HStack mt={1} spacing={1} wrap="wrap">{(item.thread_ids || []).map((threadId) => <Badge key={threadId} as="button" colorScheme="blue" onClick={() => onThread(threadId)}>Thread {threadId}</Badge>)}</HStack></Box>
              ))}
              {!summary.output[key]?.length && <Text fontSize="sm" color="gray.500">Not enough repeated evidence yet.</Text>}
            </VStack>
          </Box>
        ))}
      </Grid>
    </Box>
  );
}

export default function AssociateCoachingDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [periods, setPeriods] = useState([]);
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const periodId = searchParams.get("period") || "";
  const jobClass = searchParams.get("job_class") || "";
  const frameworkFamily = searchParams.get("framework") || "";

  const setSelection = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "job_class") next.delete("framework");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getAssociateCoachingDashboard({
        periodId: periodId || undefined,
        jobClass: jobClass || undefined,
        frameworkFamily: frameworkFamily || undefined,
      });
      setData(response);
      if (
        (!jobClass && response.selected_job_class)
        || (!frameworkFamily && response.selected_framework_family)
      ) {
        const next = new URLSearchParams(searchParams);
        if (!jobClass && response.selected_job_class) next.set("job_class", response.selected_job_class);
        if (!frameworkFamily && response.selected_framework_family) next.set("framework", response.selected_framework_family);
        setSearchParams(next, { replace: true });
      }
      if (response.selected_job_class && response.selected_framework_family) {
        refreshAssociateCoachingSummary({
          periodId: response.period?.id,
          jobClass: response.selected_job_class,
          frameworkFamily: response.selected_framework_family,
        }).catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load your coaching dashboard.");
    } finally {
      setLoading(false);
    }
  }, [frameworkFamily, jobClass, periodId, searchParams, setSearchParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getAssociateCoachingPeriods().then((result) => setPeriods(result.periods || [])).catch(() => setPeriods([]));
    getMyCoachingGoals().then(setGoals).catch(() => setGoals([]));
    getMyCoachingFeedback().then(setFeedback).catch(() => setFeedback([]));
  }, []);

  const stages = useMemo(() => (data?.aggregate?.dimensions || []).filter((item) => item.dimension_type === "stage"), [data]);
  const crossCutting = useMemo(() => (data?.aggregate?.dimensions || []).filter((item) => item.dimension_type === "cross_cutting"), [data]);
  const strengths = data?.aggregate?.intelligence?.strengths || [];
  const opportunities = data?.aggregate?.intelligence?.opportunities || [];

  return (
    <PageLayoutWithHeaderNav maxWidth="container.xl">
      <VStack align="stretch" spacing={6}>
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4} direction={{ base: "column", md: "row" }}>
          <Box><Text color="blue.600" fontSize="xs" fontWeight="bold" textTransform="uppercase">My coaching</Text><Heading mt={1} size="xl">Performance overview</Heading><Text mt={2} color="gray.500">Your role-specific coaching results and next actions.</Text></Box>
          <HStack><Select size="sm" value={periodId} onChange={(event) => setSelection("period", event.target.value)} aria-label="Coaching period"><option value="">Current rolling 28 days</option>{periods.filter((item) => !item.is_current).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</Select><Button size="sm" variant="outline" leftIcon={<MdRefresh />} onClick={load} isLoading={loading}>Refresh</Button></HStack>
        </Flex>
        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
        {loading && !data ? <Skeleton h="320px" borderRadius="xl" /> : data ? (
          <>
            <HStack spacing={1} bg="gray.100" p={1} borderRadius="lg" alignSelf="flex-start" wrap="wrap">{(data.available_job_classes || []).map((value) => <Button key={value} size="sm" variant={data.selected_job_class === value ? "solid" : "ghost"} colorScheme={data.selected_job_class === value ? "blue" : "gray"} onClick={() => setSelection("job_class", value)}>{JOB_LABELS[value] || value}</Button>)}</HStack>
            {(data.available_framework_families || []).length > 1 && <Select maxW="320px" value={data.selected_framework_family || frameworkFamily} onChange={(event) => setSelection("framework", event.target.value)} aria-label="Coaching framework">{data.available_framework_families.map((family) => <option key={family} value={family}>{family}</option>)}</Select>}
            <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
              <Stat bg="white" borderWidth="1px" borderRadius="xl" p={5}><StatLabel>Score</StatLabel><StatNumber>{percentage(data.aggregate.score)}</StatNumber><StatHelpText>Deterministic role score</StatHelpText></Stat>
              <Stat bg="white" borderWidth="1px" borderRadius="xl" p={5}><StatLabel>Coverage</StatLabel><StatNumber>{percentage(data.aggregate.coverage)}</StatNumber><StatHelpText>Observed applicable behaviors</StatHelpText></Stat>
              <Stat bg="white" borderWidth="1px" borderRadius="xl" p={5}><StatLabel>Analyzed threads</StatLabel><StatNumber>{data.aggregate.scorecard_count}</StatNumber><StatHelpText>Current reports</StatHelpText></Stat>
              <Stat bg="white" borderWidth="1px" borderRadius="xl" p={5}><StatLabel>Trend</StatLabel><StatNumber>{data.score_trend == null ? "—" : `${data.score_trend > 0 ? "+" : ""}${data.score_trend}`}</StatNumber><StatHelpText>Second half vs first half</StatHelpText></Stat>
            </Grid>
            <DimensionGrid title="Your dealership journey" dimensions={stages} />
            <DimensionGrid title="Across the conversation" description="Behaviors that span multiple journey stages." dimensions={crossCutting} />
            {(strengths.length > 0 || opportunities.length > 0) && <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>{[["Strengths", strengths, "green"], ["Focus areas", opportunities, "orange"]].map(([label, items, color]) => <Box key={label} bg={`${color}.50`} borderWidth="1px" borderColor={`${color}.100`} borderRadius="xl" p={5}><Heading size="sm">{label}</Heading><VStack align="stretch" mt={3}>{items.map((item) => <Flex key={`${item.dimension_type}:${item.dimension_id}`} justify="space-between"><Text>{item.label}</Text><Text fontWeight="bold">{percentage(item.score)}</Text></Flex>)}</VStack></Box>)}</Grid>}
            <Narrative summary={data.narrative_summary} onThread={(threadId) => navigate(`/threads/${threadId}`)} />
            <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={5}>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}><Heading size="md">Goals</Heading><VStack align="stretch" mt={3}>{goals.map((goal) => <Box key={goal.id} borderTopWidth="1px" pt={3}><Flex justify="space-between" gap={3}><Text fontWeight="semibold">{goal.title}</Text><Badge colorScheme={goal.status === "active" ? "blue" : "gray"}>{goal.status}</Badge></Flex><Text mt={1} fontSize="sm" color="gray.500">{goal.dimension_label} · current {percentage(goal.current_score)}{goal.target_score == null ? "" : ` · target ${percentage(goal.target_score)}`}</Text>{goal.dimension_current === false && <Text mt={1} fontSize="xs" color="orange.600">This goal belongs to an earlier framework version.</Text>}</Box>)}{!goals.length && <Text color="gray.500">No coaching goals yet.</Text>}</VStack></Box>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}><Heading size="md">Manager feedback</Heading><VStack align="stretch" mt={3}>{feedback.map((item) => <Box key={item.id} borderTopWidth="1px" pt={3}><Text>{item.message}</Text><Text mt={1} fontSize="xs" color="gray.500">{item.author_name || "Manager"}</Text></Box>)}{!feedback.length && <Text color="gray.500">No manager feedback yet.</Text>}</VStack></Box>
            </Grid>
            <Box bg="white" borderWidth="1px" borderRadius="xl" overflow="hidden"><Heading size="md" p={5} pb={2}>Recent coached threads</Heading><VStack align="stretch" spacing={0}>{(data.threads || []).map((thread) => <Flex key={thread.thread_id} as="button" type="button" px={5} py={3} borderTopWidth="1px" align="center" gap={4} textAlign="left" _hover={{ bg: "blue.50" }} onClick={() => navigate(`/threads/${thread.thread_id}`)}><Box flex="1"><Text fontWeight="semibold">Thread {thread.thread_id}</Text><Text fontSize="sm" color="gray.500">{new Date(thread.occurred_at).toLocaleDateString()} · {thread.framework_family}</Text></Box><Text fontWeight="bold">{percentage(thread.score)}</Text><MdChevronRight /></Flex>)}</VStack></Box>
          </>
        ) : null}
      </VStack>
    </PageLayoutWithHeaderNav>
  );
}
